import React, { useEffect } from 'react';

import ContentContainer from '@libComponents/ContentContainer';

import TableCellContainer from '@libComponents/table/TableCellContainer';
import TableContainer from '@libComponents/table/TableContainer';
import TableHeaderContainer from '@libComponents/table/TableHeaderContainer';
import {
    datasetLoadingSelector,
    entitySelector,
    createFetchPaginatedDataset
} from '@libs/datasets';
import {
    Box,
    TableBody,
    TableRow,
    Typography,
    styled,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Divider,
    Chip,
    Grid,
    Paper,
    Avatar,
    Stack,
    Tooltip,
    Alert,
    Collapse
} from '@mui/material';

import { Button, Pagination } from '@libComponents';
import DialogWrapper from '@libComponents/Dialog';

import { connect, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { DOMAIN_NAME, ROWS_PER_PAGE } from '../common';
import { APPLIED_LEAVES } from '../redux.datasets';
import { theme } from 'app/Theme';
import dayjs from 'dayjs';
import { handleLeave, getRemainingLeaves } from 'leaveManagement/redux.actions';
import { sickLeaveFlaggedChecker } from '@libs/dateTime';

import createCachedSelector from 're-reselect';

import RejectReasonForm from './RejectReasonForm';
import SelectLeaveStatus from './LeaveStatus';
import { useSearchParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import BasicMenu from 'common/components/tableHandlar/rowhandlar';
import FilterInput from '@libComponents/FilterInput';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import BadgeIcon from '@mui/icons-material/Badge';
import CategoryIcon from '@mui/icons-material/Category';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { getFileURL } from 'common/redux.actions';
import LeaveStatus from 'common/components/LeaveStatus';

const propTypes = {};

const defaultProps = {
    entity: APPLIED_LEAVES,
    domain: DOMAIN_NAME,
    loading: true
};

const StyledBox = styled(Box)(({ theme }) => ({
    width: '100%',
    overflow: 'auto',
    '& table': {
        tableLayout: 'fixed'
    },
    '& .MuiTableRow-root': {
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        }
    }
}));

// Modern Dialog Styles
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 4,
        minWidth: 600,
        maxWidth: 700,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        [theme.breakpoints.down('sm')]: {
            minWidth: 'unset',
            maxWidth: '95vw',
            margin: '16px',
            width: 'calc(100vw - 32px)'
        }
    }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    padding: '24px 32px',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
        padding: '16px 20px'
    },
    '& .MuiTypography-root': {
        fontWeight: 600,
        fontSize: '1.25rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.1rem'
        }
    }
}));

const InfoCard = styled(Paper)(({ theme }) => ({
    padding: '16px 20px',
    marginBottom: '12px',
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
        padding: '12px 16px'
    }
}));

const InfoRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    '& .icon': {
        color: theme.palette.primary.main,
        fontSize: '20px'
    }
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
    const getStatusColor = () => {
        switch (status) {
            case 'approved':
                return {
                    bg: 'transparent',
                    color: theme.palette.success.main,
                    border: theme.palette.success.main
                };
            case 'rejected':
                return {
                    bg: 'transparent',
                    color: theme.palette.error.main,
                    border: theme.palette.error.main
                };
            case 'pending':
                return {
                    bg: 'transparent',
                    color: theme.palette.warning.main,
                    border: theme.palette.warning.main
                };
            default:
                return {
                    bg: 'transparent',
                    color: theme.palette.grey[700],
                    border: theme.palette.grey[400]
                };
        }
    };

    const colors = getStatusColor();

    return {
        backgroundColor: colors.bg,
        color: colors.color,
        border: `1px solid ${colors.border}`,
        fontWeight: 600,
        fontSize: '0.875rem',
        textTransform: 'capitalize',
        padding: '4px 8px',
        height: '32px'
    };
});

// Category color map for Leave Type labels (stroke only)
const CATEGORY_COLOR_MAP = {
    Casual: theme.palette.info.main,
    Sick: theme.palette.error.main,
    Earned: theme.palette.success.main,
    Maternity: theme.palette.warning.main,
    Default: theme.palette.primary.main
};

const getCategoryColor = (name) => {
    if (!name) return CATEGORY_COLOR_MAP.Default;
    return CATEGORY_COLOR_MAP[name] || CATEGORY_COLOR_MAP.Default;
};

const DEFAULT_TABLE_DATA = {
    'employee': {
        label: 'Employee Name',
        isVisible: true,
        disabledChange: true,
        type: 'nameObject'
    },
    'employeeEmail': {
        label: 'Employee Email',
        isVisible: false,
        disabledChange: false,
        type: 'string'
    },
    'employeeId': {
        label: 'Employee ID',
        isVisible: false,
        disabledChange: false,
        type: 'string'
    },
    'categoryName': {
        label: 'Leave Type',
        isVisible: true,
        disabledChange: true,
        type: 'string'
    },
    'startDate': {
        label: 'Start Date',
        isVisible: true,
        disabledChange: false,
        type: 'date'
    },
    'endDate': {
        label: 'End Date',
        isVisible: false,
        disabledChange: false,
        type: 'date'
    },
    'totalNumberOfDays': {
        label: 'Number Of Days',
        isVisible: true,
        disabledChange: false,
        type: 'string'
    },
    'status': {
        label: 'Status',
        isVisible: true,
        disabledChange: false,
        type: 'badge'
    },
    isEmergency: {
        label: 'Is Emergency',
        isVisible: false,
        disabledChange: false,
        type: 'boolean'
    },
    updatedAt: {
        label: 'Updated Date',
        isVisible: false,
        disabledChange: false,
        type: 'date'
    },
    updatedBy: {
        label: 'Updated By',
        isVisible: false,
        disabledChange: false,
        type: 'string'
    },
    createdAt: {
        label: 'Created Date',
        isVisible: false,
        disabledChange: false,
        type: 'date'
    },
    'reason': {
        label: 'Reason',
        isVisible: false,
        disabledChange: false,
        type: 'string'
    },
    'requiredDocuments': {
        label: 'Documents',
        isVisible: true,
        disabledChange: false,
        type: 'documents'
    }
};

const totalRowsSelector = createCachedSelector(entitySelector, (resp) =>
    resp.total ? parseInt(resp.total) : 0
)((state, domain, entity) => `@data-${domain}-${entity}`);

const dataSelector = createSelector(
    (state) => entitySelector(state, DOMAIN_NAME, APPLIED_LEAVES),
    (resp) => (resp && resp.data ? resp.data : [])
);

const mapState = (state, { domain, entity }) => ({
    data: dataSelector(state, domain, entity),
    loading: datasetLoadingSelector(state, domain, entity),
    totalRows: totalRowsSelector(state, domain, entity)
});

const mapDispatch = (dispatch, { entity }) => ({
    fetchDataset: ({
        offset = '',
        limit = ROWS_PER_PAGE,
        shouldAppend = false,
        filter
    }) =>
        dispatch(
            createFetchPaginatedDataset({
                entity: entity,
                domain: DOMAIN_NAME
            })({
                limit,
                offset,
                shouldAppend,
                'sort-descending': true,
                filterKey: filter
            })
        )
});

const ALL_STATUS = ['approved', 'rejected', 'pending'];

const AppliedLeaves = connect(
    mapState,
    mapDispatch
)(({ fetchDataset, data, totalRows = 0 }) => {
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const statusParam = searchParams.get('status');
        const searchParam = searchParams.get('search') || '';

        const filter = {};
        // Default to 'pending' if no status param is provided
        if (statusParam && ALL_STATUS.includes(statusParam)) {
            filter.status = statusParam;
            setFilterValue(statusParam);
        } else {
            filter.status = 'pending';
            // filterValue is already initialized to 'pending'
        }
        if (searchParam) {
            filter.searchTerm = searchParam;
            setSearchTerm(searchParam);
        }

        fetchDataset({
            offset: '',
            filter: filter
        });
    }, [fetchDataset, searchParams]);

    const RXDispatch = useDispatch();
    const [open, setOpen] = React.useState(false);
    const [openReject, setOpenReject] = React.useState(false);
    const [rejectPayload, setRejectPayload] = React.useState(null);
    const [payload, setSelectedPayload] = React.useState(0);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [filterValue, setFilterValue] = React.useState('pending');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [tableData, setTableData] = React.useState(DEFAULT_TABLE_DATA);
    const [pageSize, setPageSize] = React.useState(ROWS_PER_PAGE);
    const [leaveBalance, setLeaveBalance] = React.useState([]);
    const [leaveBalanceExpanded, setLeaveBalanceExpanded] = React.useState(false);
    const [leaveBalanceFetched, setLeaveBalanceFetched] = React.useState(false);


    const handleLinkClick = async (e, docUrl) => {
        e.preventDefault();
        RXDispatch(getFileURL({
            handleSubmit, payload: { url: docUrl }
        }));
    };

    const handleSubmit = (resp) => {
        if (resp.type === "success") {
            window.open(resp.url, "_blank");
        }
    };

    // is visible then only take that object
    const TABLE_DATA = Object.entries(tableData).reduce((acc, [key, value]) => {
        if (value.isVisible) {
            acc[key] = value;
        }
        return acc;
    }, {});

    const handleClick = (row, action) => {
        if (action === 'reject') {
            setOpenReject(true);
            setRejectPayload(row);
        } else {
            RXDispatch(
                handleLeave({
                    handleSubmit: handleLeaveRequest,
                    payload: { row, action }
                })
            );
        }
    };
    const handleLeaveRequest = (payload) => { };

    const handleChangePage = (event, newPage) => {
        const filter = {};
        if (searchTerm) filter.searchTerm = searchTerm;
        if (filterValue) filter.status = filterValue;

        fetchDataset({
            offset: (newPage - 1) * pageSize,
            limit: pageSize,
            filter: filter
        });
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (pageSize) => {
        setPageSize(pageSize);
        setCurrentPage(1);

        const filter = {};
        if (searchTerm) filter.searchTerm = searchTerm;
        if (filterValue) filter.status = filterValue;

        fetchDataset({
            offset: 0 * pageSize,
            limit: pageSize,
            filter: filter
        });
    };

    const viewLeaveDetails = (row) => {
        setOpen(true);
        setSelectedPayload(row);
        // Reset leave balance state when opening new popup
        setLeaveBalance([]);
        setLeaveBalanceExpanded(false);
        setLeaveBalanceFetched(false);
    };

    const handleLeaveBalanceToggle = () => {
        const newExpandedState = !leaveBalanceExpanded;
        setLeaveBalanceExpanded(newExpandedState);

        // Fetch data only on first expand
        if (newExpandedState && !leaveBalanceFetched) {
            if (payload?.employeeEmail || payload?.employee?.userId) {
                fetchLeaveBalance(payload?.employeeEmail || payload?.employee?.userId);
            }
        }
    };

    const fetchLeaveBalance = (email) => {
        RXDispatch(
            getRemainingLeaves({
                payload: { email },
                handleSubmit: handleLeaveBalanceSubmit
            })
        );
    };

    const handleLeaveBalanceSubmit = (data) => {
        if (data && data.length) {
            setLeaveBalance(data);
            setLeaveBalanceFetched(true);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleRejectClose = () => {
        setOpenReject(false);
    };

    const handleReject = () => {
        handleClick(payload, 'reject');
        handleClose();
    };

    const handleApprove = () => {
        handleClick(payload, 'approve');
        handleClose();
    };

    const handleFilterChange = (value) => {
        const filter = {};
        if (searchTerm) filter.searchTerm = searchTerm;
        if (value) filter.status = value;

        fetchDataset({
            offset: '',
            filter: filter
        });
        setFilterValue(value);
        setCurrentPage(1);
    };

    const handleSearch = (value) => {
        const filter = {};
        if (value) filter.searchTerm = value;
        if (filterValue) filter.status = filterValue;

        fetchDataset({
            offset: '',
            limit: pageSize,
            filter: filter
        });
        setSearchTerm(value);
        setCurrentPage(1);
    };

    if (!data) return null;

    return (
        <ContentContainer
            title='All Leaves'
            headerBg={theme.palette.background.mainHeader}
            AdornmentComponent={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FilterInput
                        name='employee-email'
                        handleSearch={handleSearch}
                    />
                    <SelectLeaveStatus
                        sx={(theme) => ({
                            backgroundColor: theme.palette.background.white,
                            maxWidth: 300,
                            mt: 1
                        })}
                        input={{
                            name: 'status',
                            onChange: handleFilterChange,
                            value: filterValue
                        }}
                    />
                    <BasicMenu
                        tableData={tableData}
                        setTableData={setTableData}
                    />
                </Box>
            }
        >
            <StyledBox>
                <TableContainer>
                    <TableHeaderContainer>
                        {Object.entries(TABLE_DATA).map(([key, value], i) => (
                            <TableCellContainer
                                sx={{ fontWeight: 'bold' }}
                                typography='caption'
                                key={key}
                            >
                                {value.label}
                            </TableCellContainer>
                        ))}
                        <TableCellContainer
                            align='center'
                            sx={{ pr: 4, fontWeight: 'bold' }}
                        >
                            Action
                        </TableCellContainer>
                    </TableHeaderContainer>
                    <TableBody>
                        {data.map((row, i) => (
                            <TableRow
                                key={i}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => {
                                    viewLeaveDetails(row);
                                }}
                            >
                                {Object.entries(TABLE_DATA).map(
                                    ([key, value], index) => {
                                        switch (value.type) {
                                            case 'file':
                                                return null;
                                            case 'documents':
                                                return (
                                                    <TableCellContainer
                                                        key={key}
                                                        sx={{
                                                            color: sickLeaveFlaggedChecker(
                                                                dayjs(
                                                                    row.startDate
                                                                ).format('ddd'),
                                                                row
                                                            )
                                                        }}
                                                    >
                                                        {row[key] && row[key].length > 0
                                                            ? `${row[key].length} document${row[key].length > 1 ? 's' : ''}`
                                                            : 'No documents'
                                                        }
                                                    </TableCellContainer>
                                                );
                                            case 'date':
                                                return (
                                                    <TableCellContainer
                                                        key={key}
                                                        sx={{
                                                            color: sickLeaveFlaggedChecker(
                                                                dayjs(
                                                                    row[key]
                                                                ).format('ddd'),
                                                                row
                                                            )
                                                        }}
                                                    >
                                                        {dayjs(row[key]).format(
                                                            'YYYY-MM-DD (ddd)'
                                                        )}
                                                    </TableCellContainer>
                                                );
                                            case 'boolean':
                                                return (
                                                    <TableCellContainer
                                                        key={key}
                                                        sx={{
                                                            color: sickLeaveFlaggedChecker(
                                                                dayjs(
                                                                    row[key]
                                                                ).format('ddd'),
                                                                row
                                                            )
                                                        }}
                                                    >
                                                        {row[key] ? (
                                                            <CheckIcon
                                                                sx={{
                                                                    color: theme
                                                                        .palette
                                                                        .success
                                                                        .main,
                                                                    fontWeight:
                                                                        'bold'
                                                                }}
                                                            />
                                                        ) : (
                                                            ''
                                                        )}
                                                    </TableCellContainer>
                                                );
                                            case 'nameObject':
                                                return (
                                                    <TableCellContainer
                                                        key={key}
                                                        sx={{
                                                            color: (theme) => theme.palette.text.primary
                                                        }}
                                                    >
                                                        {row[key].firstname
                                                            ? row[key]
                                                                .firstname +
                                                            ' '
                                                            : ''}
                                                        {row[key].middlename
                                                            ? row[key]
                                                                .middlename +
                                                            ' '
                                                            : ''}
                                                        {row[key].lastname}
                                                    </TableCellContainer>
                                                );
                                            case 'badge':
                                                const statusIcon = <LeaveStatus leaveStatus={row[key]} />;

                                                const statusLabel = row[key]
                                                    ? row[key].charAt(0).toUpperCase() + row[key].slice(1)
                                                    : 'N/A';

                                                return (
                                                    <TableCellContainer
                                                        key={key}
                                                    >
                                                        <Tooltip title={statusLabel} arrow placement="top">
                                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                                                                {statusIcon}
                                                            </Box>
                                                        </Tooltip>
                                                    </TableCellContainer>
                                                );
                                            default:
                                                // Special-case Number Of Days column to ensure consistent bold sizing
                                                if (key === 'totalNumberOfDays') {
                                                    const days = row[key] || row?.duration || 'N/A';
                                                    return (
                                                        <TableCellContainer
                                                            key={index}
                                                            sx={{
                                                                color: sickLeaveFlaggedChecker(dayjs(row[key]).format('ddd'), row)
                                                            }}
                                                        >
                                                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                                                                {days}
                                                            </Typography>
                                                        </TableCellContainer>
                                                    );
                                                }

                                                // Special-case Leave Type column to show stroked label with category color
                                                if (key === 'categoryName') {
                                                    const label = row[key] || (row?.category?.name) || 'N/A';
                                                    const color = getCategoryColor(label);
                                                    return (
                                                        <TableCellContainer
                                                            key={index}
                                                            sx={{
                                                                color: sickLeaveFlaggedChecker(dayjs(row[key]).format('ddd'), row)
                                                            }}
                                                        >
                                                            <Chip
                                                                label={label}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    mt: 0,
                                                                    fontWeight: 600,
                                                                    borderColor: color,
                                                                    color: color,
                                                                    backgroundColor: 'transparent'
                                                                }}
                                                            />
                                                        </TableCellContainer>
                                                    );
                                                }
                                                return (
                                                    <TableCellContainer
                                                        key={index}
                                                        sx={{
                                                            color: sickLeaveFlaggedChecker(
                                                                dayjs(row[key]).format('ddd'),
                                                                row
                                                            )
                                                        }}
                                                    >
                                                        {row[key]}
                                                    </TableCellContainer>
                                                );
                                        }
                                    }
                                )}
                                <TableCellContainer
                                    align='center'
                                    sx={{ pr: 4 }}
                                >
                                    <Tooltip title="View Details" arrow placement="top">
                                        <IconButton
                                            sx={(theme) => ({
                                                '&:hover': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            })}
                                            onClick={() => {
                                                viewLeaveDetails(row);
                                            }}
                                            size='small'
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCellContainer>
                            </TableRow>
                        ))}
                    </TableBody>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        count={Math.ceil(totalRows / pageSize)}
                        onChange={handleChangePage}
                        page={currentPage}
                        sx={{ display: 'flex', justifyContent: 'center' }}
                        isRowsPerPageVarient={true}
                        rowsPerPageProps={{
                            name: 'appliedLeaves',
                            onChange: handleRowsPerPageChange,
                            value: pageSize
                        }}
                    />
                </Box>
            </StyledBox>

            {/* Modern Leave Details Dialog */}
            <StyledDialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
            >
                <StyledDialogTitle>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" gap={1.5}>
                            <EventIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                                Leave Request Details
                            </Typography>
                        </Stack>
                        <IconButton
                            onClick={handleClose}
                            sx={(theme) => ({
                                color: theme.palette.text.primary,
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover
                                }
                            })}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </StyledDialogTitle>

                <DialogContent sx={(theme) => ({
                    padding: '32px',
                    paddingTop: '28px !important',
                    backgroundColor: theme.palette.background.paper,
                    '@media (max-width: 600px)': {
                        padding: '20px 16px',
                        paddingTop: '20px !important'
                    }
                })}>
                    {/* Status Badge */}
                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <StatusChip
                                label={payload?.status || 'Pending'}
                                status={payload?.status}
                            />
                            {payload?.isEmergency && (
                                <Chip
                                    label="Emergency"
                                    size="small"
                                    sx={{
                                        backgroundColor: 'transparent',
                                        color: theme.palette.error.main,
                                        fontWeight: 600,
                                        border: `1px solid ${theme.palette.error.main}`,
                                        height: '32px'
                                    }}
                                />
                            )}
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTimeIcon sx={{ fontSize: 16 }} />
                                Applied: {payload?.createdAt ? dayjs(payload.createdAt).format('MMM DD, YYYY') : 'N/A'}
                            </Typography>
                            {payload?.updatedAt && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AccessTimeIcon sx={{ fontSize: 16 }} />
                                    Updated: {dayjs(payload.updatedAt).format('MMM DD, YYYY')}
                                </Typography>
                            )}
                        </Stack>
                    </Box>

                    {/* Employee Information */}
                    <InfoCard elevation={0}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Employee Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <InfoRow>
                                    <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
                                        {payload?.employee?.initial?.[0] || <PersonIcon />}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            Full Name
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                            {payload?.employee?.initial ? `${payload.employee.initial} ` : ''}
                                            {payload?.employee?.firstname || payload?.employee?.middlename || payload?.employee?.lastname
                                                ? `${payload?.employee?.firstname || ''} ${payload?.employee?.middlename || ''} ${payload?.employee?.lastname || ''}`.trim()
                                                : 'N/A'
                                            }
                                        </Typography>
                                    </Box>
                                </InfoRow>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InfoRow>
                                    <EmailIcon className="icon" />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            Email
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {payload?.employeeEmail || payload?.employee?.userId || 'N/A'}
                                        </Typography>
                                    </Box>
                                </InfoRow>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InfoRow>
                                    <BadgeIcon className="icon" />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            Employee ID
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {payload?.employeeId || payload?.employee?.employeeId || 'N/A'}
                                        </Typography>
                                    </Box>
                                </InfoRow>
                            </Grid>
                            {payload?.employee?.gender && (
                                <Grid item xs={12} sm={6}>
                                    <InfoRow>
                                        <PersonIcon className="icon" />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                Gender
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {payload.employee.gender}
                                            </Typography>
                                        </Box>
                                    </InfoRow>
                                </Grid>
                            )}
                            {payload?.employee?.reportingManager && (
                                <Grid item xs={12} sm={6}>
                                    <InfoRow>
                                        <PersonIcon className="icon" />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                Reporting Manager
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {payload.employee.reportingManager}
                                            </Typography>
                                        </Box>
                                    </InfoRow>
                                </Grid>
                            )}
                            {payload?.updatedBy && (
                                <Grid item xs={12}>
                                    <InfoRow>
                                        <PersonIcon className="icon" />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                Updated By
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {payload.updatedBy}
                                            </Typography>
                                        </Box>
                                    </InfoRow>
                                </Grid>
                            )}
                        </Grid>
                    </InfoCard>

                    {/* Leave Balance Card - Collapsible */}
                    <InfoCard elevation={0}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                userSelect: 'none'
                            }}
                            onClick={handleLeaveBalanceToggle}
                        >
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <AccountBalanceWalletIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                                Leave Balance
                            </Typography>
                            <IconButton size="small" sx={{ padding: 0 }}>
                                {leaveBalanceExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>

                        <Collapse in={leaveBalanceExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 2 }}>
                                {leaveBalance && leaveBalance.length > 0 ? (
                                    <>
                                        {/* Summary label (full-width) that accounts for the current pending application */}
                                        <Box sx={{ mb: 1.5, px: 0 }}>
                                            {(() => {
                                                const matched = leaveBalance.find(lb => (
                                                    (lb?.category?.name && payload?.category?.name && lb.category.name === payload.category.name) ||
                                                    (lb?.category?.name && payload?.categoryName && lb.category.name === payload.categoryName)
                                                ));

                                                if (!matched) return null;

                                                const total = matched.totalDays || 0;
                                                const used = (matched.totalDays || 0) - (matched.remainingDays || 0);
                                                const pending = (payload && ((payload?.categoryName === matched.category.name) || (payload?.category && payload.category.name === matched.category.name)) && (payload?.totalNumberOfDays || payload?.duration)) ? (payload?.totalNumberOfDays || payload?.duration) : 0;

                                                // Adjust calculations depending on leave status
                                                const status = (payload?.status || '').toString().toLowerCase();
                                                // base used is what the API reports (total - remaining)
                                                const baseUsed = used;
                                                let adjustedUsed = baseUsed;
                                                let note = '';

                                                // If the application is pending, show the pending days as an upcoming addition
                                                if (pending && status === 'pending') {
                                                    adjustedUsed = baseUsed + pending;
                                                    note = ` (includes ${pending} pending)`;
                                                }

                                                // If the application was rejected, it should not be counted — baseUsed already excludes it
                                                if (pending && status === 'rejected') {
                                                    adjustedUsed = baseUsed; // ensure pending isn't counted
                                                    note = ' (application rejected days not counted)';
                                                }

                                                // If the application was approved, the backend should already have adjusted remainingDays
                                                // so baseUsed already includes the approved days; do not add pending again.
                                                const adjustedRemaining = Math.max(0, total - adjustedUsed);

                                                return (
                                                    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', width: '100%', padding: '8px 10px', borderRadius: 1, border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
                                                        <InfoIcon sx={{ fontSize: 18, color: theme.palette.primary.main, mt: '2px' }} />
                                                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, lineHeight: 1.4 }}>
                                                            For <strong>{matched.category?.name || 'this category'}</strong>: allowance is <strong>{total}</strong> days. Already used <strong>{baseUsed}</strong> day{baseUsed !== 1 ? 's' : ''}{status === 'pending' && pending ? `, plus ${pending} pending (current application)` : ''}{status === 'approved' && pending ? ` This application has been approved and counted in the used days.` : ''}{status === 'rejected' && pending ? ` This application was rejected and its ${pending} day${pending !== 1 ? 's' : ''} are not counted.` : ''} So total used: <strong>{adjustedUsed}</strong> of <strong>{total}</strong> days. Remaining: <strong>{adjustedRemaining}</strong> day{adjustedRemaining !== 1 ? 's' : ''}{note ? ` —${note.replace(/^ /, '')}` : ''}.
                                                        </Typography>
                                                    </Box>
                                                );
                                            })()}
                                        </Box>

                                        <Grid container spacing={1}>
                                            {leaveBalance
                                                .filter(leave => leave?.totalDays !== undefined && leave?.totalDays !== 0)
                                                .map((leave, index) => {
                                                    const usedDays = (leave?.totalDays || 0) - (leave?.remainingDays || 0);
                                                    return (
                                                        <Grid item xs={12} sm={6} key={index}>
                                                            <Box sx={{
                                                                padding: '8px 10px',
                                                                borderRadius: 1,
                                                                backgroundColor: theme.palette.background.paper,
                                                                border: `1px solid ${theme.palette.divider}`
                                                            }}>
                                                                {/* Category name: use same size/weight as other labels */}
                                                                <Typography variant="body2" sx={{ fontWeight: 400, color: theme.palette.text.primary, mb: 0.75 }}>
                                                                    {leave?.category?.name || 'N/A'}
                                                                </Typography>
                                                                {/* Used / Total: orange color */}
                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.warning.main, mb: 0.25 }}>
                                                                    {usedDays.toFixed(2)} / {leave?.totalDays !== undefined ? leave.totalDays : 'N/A'} days
                                                                </Typography>
                                                                {/* Remaining days: consistent caption style */}
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                    {leave?.remainingDays !== undefined
                                                                        ? `${leave.remainingDays} days left`
                                                                        : 'N/A'
                                                                    }
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    );
                                                })}
                                        </Grid>
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                                        Loading leave balance...
                                    </Typography>
                                )}
                            </Box>
                        </Collapse>
                    </InfoCard>

                    {/* Leave Details */}
                    <InfoCard elevation={0}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Leave Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <InfoRow>
                                    <CategoryIcon className="icon" />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            Leave Type
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, color: theme.palette.primary.main }}>
                                            {payload?.categoryName || payload?.category?.name || 'N/A'}
                                        </Typography>
                                    </Box>
                                </InfoRow>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InfoRow>
                                    <CalendarTodayIcon className="icon" />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            Start Date
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                            {payload?.startDate ? dayjs(payload.startDate).format('MMM DD, YYYY (ddd)') : 'N/A'}
                                        </Typography>
                                    </Box>
                                </InfoRow>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InfoRow>
                                    <CalendarTodayIcon className="icon" />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            End Date
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                            {payload?.endDate ? dayjs(payload.endDate).format('MMM DD, YYYY (ddd)') : 'N/A'}
                                        </Typography>
                                    </Box>
                                </InfoRow>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InfoRow>
                                    <AccessTimeIcon className="icon" />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            Total Days
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {payload?.totalNumberOfDays || payload?.duration
                                                ? `${payload?.totalNumberOfDays || payload?.duration} ${(payload?.totalNumberOfDays || payload?.duration) === 1 ? 'day' : 'days'}`
                                                : 'N/A'
                                            }
                                        </Typography>
                                    </Box>
                                </InfoRow>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InfoRow>
                                    <AccessTimeIcon className="icon" />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            Day Type
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                                            {(payload?.totalNumberOfDays === 1 || payload?.duration === 1)
                                                ? 'Full Day'
                                                : (payload?.totalNumberOfDays === 0.5 || payload?.duration === 0.5)
                                                    ? 'Half Day'
                                                    : (payload?.totalNumberOfDays === 0.25 || payload?.duration === 0.25)
                                                        ? 'Quarter Day'
                                                        : payload?.isHalfDay
                                                            ? 'Half Day'
                                                            : 'Full Day'
                                            }
                                        </Typography>
                                    </Box>
                                </InfoRow>
                            </Grid>
                            {payload?.category?.totalPerBusinessYear && (
                                <Grid item xs={12} sm={6}>
                                    <InfoRow>
                                        <EventIcon className="icon" />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                Total Allowed Per Year
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {payload.category.totalPerBusinessYear} days
                                            </Typography>
                                        </Box>
                                    </InfoRow>
                                </Grid>
                            )}
                        </Grid>
                    </InfoCard>

                    {/* Reason/Description */}
                    <InfoCard elevation={0}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            <DescriptionIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                            Reason for Leave
                        </Typography>
                        <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.primary' }}>
                            {payload?.reason || 'No reason provided'}
                        </Typography>
                    </InfoCard>

                    {/* Comment Section */}
                    {payload?.comment && (
                        <InfoCard elevation={0}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                💬 Comment
                            </Typography>
                            <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.primary' }}>
                                {payload.comment}
                            </Typography>
                        </InfoCard>
                    )}

                    {/* Documents */}
                    <InfoCard elevation={0}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            <AttachFileIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                            Attachments {payload?.requiredDocuments && payload.requiredDocuments.length > 0 ? `(${payload.requiredDocuments.length})` : ''}
                        </Typography>
                        {payload?.requiredDocuments && payload.requiredDocuments.length > 0 ? (
                            <Stack spacing={1}>
                                {payload.requiredDocuments.map((docUrl, docIndex) => (
                                    <Box
                                        key={docIndex}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            padding: '8px 12px',
                                            borderRadius: 1,
                                            backgroundColor: theme.palette.background.paper,
                                            border: `1px solid ${theme.palette.divider}`
                                        }}
                                    >
                                        <AttachFileIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                                        <a
                                            href={docUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                color: theme.palette.primary.main,
                                                textDecoration: 'none',
                                                fontWeight: 500,
                                                fontSize: '0.875rem',
                                                flex: 1
                                            }}
                                            onClick={(e) => handleLinkClick(e, docUrl)}
                                        >
                                            Document {docIndex + 1}
                                        </a>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No documents attached
                            </Typography>
                        )}
                    </InfoCard>

                    {/* Employees to Notify Section */}
                    {payload?.employeesToNotify && payload.employeesToNotify.length > 0 && (
                        <InfoCard elevation={0}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <EmailIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                                Employees to Notify
                            </Typography>
                            <Stack spacing={1.5}>
                                {payload.employeesToNotify.map((employee, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            padding: '10px 12px',
                                            borderRadius: 1,
                                            backgroundColor: theme.palette.background.paper,
                                            border: `1px solid ${theme.palette.divider}`
                                        }}
                                    >
                                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36 }}>
                                            {(employee?.firstname?.[0] || '') + (employee?.lastname?.[0] || '')}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {employee?.initial ? `${employee.initial} ` : ''}
                                                {`${employee?.firstname || ''} ${employee?.middlename || ''} ${employee?.lastname || ''}`.trim()}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {employee?.userId || 'N/A'} • {employee?.employeeId || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </InfoCard>
                    )}

                    {/* Rejection Reason */}
                    {payload?.rejectReason && (
                        <InfoCard elevation={0} sx={{ backgroundColor: theme.palette.error.alert, borderColor: theme.palette.error.light }}>
                            <Typography variant="subtitle2" color="error" sx={{ mb: 1, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                ⚠️ Rejection Reason
                            </Typography>
                            <Typography variant="body2" color="error.dark" sx={{ lineHeight: 1.7 }}>
                                {payload.rejectReason}
                            </Typography>
                        </InfoCard>
                    )}
                </DialogContent>

                <DialogActions sx={(theme) => ({
                    padding: '20px 32px',
                    backgroundColor: theme.palette.background.paper,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    gap: 1,
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    '@media (max-width: 600px)': {
                        padding: '16px 20px'
                    }
                })}>
                    {/* Info message for non-pending status */}
                    {payload?.status === 'approved' && (
                        <Alert severity="success" sx={{ mb: 1 }}>
                            This leave is already <strong>approved</strong>{payload?.updatedBy ? ` by ${payload.updatedBy}` : ''}, but you can reject it if needed.
                        </Alert>
                    )}
                    {payload?.status === 'rejected' && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                            This leave is already <strong>rejected</strong>{payload?.updatedBy ? ` by ${payload.updatedBy}` : ''}, but you can approve it if needed.
                        </Alert>
                    )}

                    {/* Action buttons */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                        '@media (max-width: 600px)': {
                            flexDirection: 'column',
                            '& button': {
                                width: '100%'
                            }
                        }
                    }}>
                        {payload?.status === 'pending' && (
                            <>
                                <Button
                                    onClick={handleReject}
                                    variant="outlined"
                                    color="error"
                                    sx={{ minWidth: 100 }}
                                >
                                    Reject
                                </Button>
                                <Button
                                    onClick={handleApprove}
                                    variant="contained"
                                    color="success"
                                    sx={{
                                        minWidth: 100,
                                    }}
                                >
                                    Approve
                                </Button>
                            </>
                        )}
                        {payload?.status === 'approved' && (
                            <Button
                                onClick={handleReject}
                                variant="outlined"
                                color="error"
                                sx={{ minWidth: 100 }}
                            >
                                Reject
                            </Button>
                        )}
                        {payload?.status === 'rejected' && (
                            <Button
                                onClick={handleApprove}
                                variant="contained"
                                color="success"
                                sx={{
                                    minWidth: 100,
                                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                                }}
                            >
                                Approve
                            </Button>
                        )}
                    </Box>
                </DialogActions>
            </StyledDialog>

            <DialogWrapper
                open={openReject}
                onClose={handleRejectClose}
                sx={{ textAlign: 'center' }}
            >
                <Box sx={{ 'minWidth': '600px', minHeight: '200px' }}>
                    <RejectReasonForm
                        rejectPayload={rejectPayload}
                        onReject={handleRejectClose}
                    />
                </Box>
            </DialogWrapper>
        </ContentContainer>
    );
});

AppliedLeaves.propTypes = propTypes;
AppliedLeaves.defaultProps = defaultProps;

export default AppliedLeaves;